import React, { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import { publicRoutes, routes, selectedPaths } from '../assets/routes';
import Home from '../components/pages/Home';
import Profile from '../components/pages/Profile';
import Login from '../components/pages/Login';
import NavBar from '../components/common/NavBar';
import SliderDialog from '../components/common/SliderDialog';
import Header from '../components/common/Header';
import Search from '../components/pages/Search';
import Gift from '../components/pages/Gift';
import EditProfile from '../components/pages/EditProfile';
import Announcement from '../components/pages/Announcement';
import AnnouncementsList from '../components/pages/AnnouncementsList';
import GiveKudos from '../components/pages/GiveKudos/GiveKudos';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { handleBackButton } from './utils';
import { useDispatch, useSelector } from 'react-redux';
import { closeSliderDialogAction } from '../reactRedux/Actions/showSliderDialogAction';
import useDesktopDisplay from '../hooks/useDesktopDisplay';
import SideBar from '../components/common/SideBar';
import ChangePassword from '../components/pages/ChangePassword';
import Settings from '../components/pages/Settings';
import FeedbackRequestCreate from '../components/pages/FeedbackRequestCreate';
import FeedbackRequestsList from '../components/pages/FeedbackRequestsList';
import FeedbackRequest from '../components/pages/FeedbackRequest';
import NotificationsList from '../components/pages/NotificationsList';
import KudosDetail from '../components/pages/KudosDetail';
import GreetingMoodTracker from '../components/pages/GreetingMoodTracker';
import SettingsNotifications from '../components/pages/SettingsNotifications';
import Welcome from '../components/pages/Welcome';
import SurveyList from '../components/pages/SurveyList';
import Survey from '../components/pages/Survey';
import Birthday from '../components/pages/Birthday';
import Occasions from '../components/pages/Occasions';
import Register from '../components/pages/register';
import ResetPassword from '../components/pages/ResetPassword';
import LanguageSync from '../i18n/LanguageSync';
import { useTranslation } from '../i18n/LanguageProvider';

const MoodTracker = React.lazy(() =>
  import(/*webpackPrefetch: true*/ '../components/pages/MoodTracker')
);

const ConversationUsers = React.lazy(() =>
  import('../components/pages/ConversationUsers')
);

const Router = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();
  const { isRtl } = useTranslation();

  const hasPopup = useSelector((state) => state.sliderDialogReducer.show);
  const closeDialog = () => dispatch(closeSliderDialogAction());

  handleBackButton(hasPopup, closeDialog);

  useEffect(() => {
    if (history.length <= 2) {
      const path = history.location.pathname;
      history.push('/home');
      history.push(path);
    }
  }, [history]);
  return (
    <React.Fragment>
      <SliderDialog />

      <ToastContainer
        position={
          isDesktop ? (isRtl ? 'bottom-left' : 'bottom-right') : 'top-center'
        }
        autoClose={4000}
        newestOnTop={isDesktop}
        rtl={isRtl}
        closeButton={isDesktop}
        pauseOnHover
        pauseOnFocusLoss
        draggable
        transition={Slide}
        hideProgressBar={false}
      />

      <PrivateRoute
        exact
        path={selectedPaths(publicRoutes)}
        component={LanguageSync}
      />

      {!isDesktop && (
        <PrivateRoute
          exact
          path={selectedPaths([...publicRoutes, 'WELCOME'])}
          component={Header}
        />
      )}
      {isDesktop && (
        <PrivateRoute
          exact
          path={selectedPaths([...publicRoutes, 'WELCOME'])}
          component={SideBar}
        />
      )}
      <Suspense fallback={<div></div>}>
        <Switch>
          <Route exact path={routes.REGISTER} component={Register} />
          <Route exact path={routes.LOGIN} component={Login} />
          <Route exact path={routes.RESETPASSWORD} component={ResetPassword} />

          <PrivateRoute exact path={routes.HOME} component={Home} />
          <PrivateRoute exact path={routes.PROFILE} component={Profile} />
          <PrivateRoute
            exact
            path={routes.ANNOUNCEMENTS}
            component={AnnouncementsList}
          />
          <PrivateRoute
            exact
            path={routes.ANNOUNCEMENT}
            component={Announcement}
          />
          <PrivateRoute exact path={routes.SEARCH} component={Search} />
          <PrivateRoute exact path={routes.GIFT} component={Gift} />
          <PrivateRoute exact path={routes.GIVE_KUDOS} component={GiveKudos} />
          <PrivateRoute exact path={routes.EDIT} component={EditProfile} />
          <PrivateRoute exact path={routes.SETTINGS} component={Settings} />
          <PrivateRoute
            exact
            path={routes.CHANGE_PASS}
            component={ChangePassword}
          />
          <PrivateRoute
            exact
            path={routes.SETTINGS_NOTIFICATIONS}
            component={SettingsNotifications}
          />
          <PrivateRoute
            exact
            path={routes.FEEDBACKS}
            component={ConversationUsers}
          />
          <PrivateRoute
            exact
            path={routes.USER_CONVERSATIONS}
            component={FeedbackRequestsList}
          />
          <PrivateRoute
            exact
            path={routes.FEEDBACK}
            component={FeedbackRequest}
          />
          <PrivateRoute
            exact
            path={routes.FEEDBACK_REQUEST_CREATE}
            component={FeedbackRequestCreate}
          />
          <PrivateRoute
            exact
            path={routes.NOTIFICATION_LIST}
            component={NotificationsList}
          />
          <PrivateRoute exact path={routes.KUDOS} component={KudosDetail} />
          <PrivateRoute
            exact
            path={routes.MOOD_TRACKER}
            component={MoodTracker}
          />
          <PrivateRoute
            exact
            path={routes.GREETING_MOOD_TRACKER}
            component={GreetingMoodTracker}
          />
          <PrivateRoute exact path={routes.WELCOME} component={Welcome} />
          <PrivateRoute exact path={routes.SURVEYS} component={SurveyList} />
          <PrivateRoute exact path={routes.SURVEY} component={Survey} />
          <PrivateRoute exact path={routes.BIRTHDAY} component={Birthday} />
          <PrivateRoute exact path={routes.OCCASIONS} component={Occasions} />
          <Redirect
            to={{
              pathname: routes.HOME,
              state: { from: history.location.pathname }
            }}
          />
        </Switch>
      </Suspense>

      {!isDesktop && (
        <PrivateRoute
          exact
          path={selectedPaths([
            ...publicRoutes,
            'ANNOUNCEMENT',
            'GIVE_KUDOS',
            'EDIT',
            'CHANGE_PASS',

            'FEEDBACK_REQUEST_CREATE',
            'FEEDBACK',
            'KUDOS',
            // 'MOOD_TRACKER',

            'WELCOME',
            'SURVEY'
          ])}
          component={NavBar}
        />
      )}
    </React.Fragment>
  );
};

Router.propTypes = {};

export default Router;
